import { PointOfImpact } from "pages/DamageLocation";

export type DigitalJourney = {
  // personal info
  firstNamePh: string;
  lastNamePh: string;
  phoneNumberPh: string;
  emailPh: string;
  licensePlatePh: string;

  // main accident info
  accidentDateTime?: string | null;
  accidentLocationId?: number | null;
  accidentLocation?: DigitalJourneyAccidentLocation | null;
  accidentType?: AccidentType | null;
  vandalismSeverity?: VandalismSeverity | null;
  naturalEventType?: NaturalEventType | null;
  insuranceType?: InsuranceType | null;
  numInjuredPeople?: number | null;
  driverPresent?: boolean | null;
  informationOnOtherDriver?: boolean | null;
  additionalDetails?: string | null;
  numVehiclesInvolved?: number | null;
  informationOnOtherVehicle?: boolean | null;
  claimHandlingDecision?: ClaimHandlingDecision | null;
  bodyShopId?: number | null;
  bodyShop?: DigitalJourneyBodyShop | null;
  cai?: CaiEnum | null;
  authoritiesIntervention?: AuthoritiesIntervention | null;
  accidentResponsibility?: AccidentResponsibility | null;

  // photos
  caiPhotos?: DocumentPhoto[] | null;
  hospitalReportPhotos?: DocumentPhoto[] | null;
  authoritiesPhotos?: DocumentPhoto[] | null;

  // vehicle info
  vehicleTypePh?: VehicleType | null;
  pointsOfImpactPh?: PointOfImpact[] | null;
  mileage?: number | null;
  airbagDeployed?: boolean | null;
  isDrivable?: boolean | null;
  pointsOfImpactGlass?: PointOfImpactGlass[];
  vehicleCircumstancePh?: Circumstance | null;
  personalCodePh?: string | null;
  postalCodePh?: string | null;

  // other driver info
  otherDriverFirstName?: string | null;
  otherDriverLastName?: string | null;
  otherDriverPersonalCode?: string | null;
  otherVehicleInsurance?: string | null;
  otherVehicleLicensePlate?: string | null;
  otherVehiclePointsOfImpact?: PointOfImpact[] | null;
  otherVehicleCircumstance?: Circumstance | null;
  otherVehicleType?: VehicleType | null;

  // injured people
  injuredPersons?: InjuredPerson[] | null;

  // signature
  signaturePhoto?: DocumentPhoto | null;
};

export type DigitalJourneyAccidentLocation = {
  city: string;
  postalCode?: string;
  province: string;
  country: string;
};

export type DigitalJourneyBodyShop = {
  id: number;
  name: string;
  region: string;
  province: string;
  city: string;
  address: string;
  postalCode: string;
  phoneNumber: string;
  email: string;
  vatNumber: string;
  enabled?: boolean;
};

export type DocumentPhoto = {
  format: string;
  path: string;
  bucket: string;
  key: string;
  clientImageId: string;
};

export type InjuredPerson = {
  firstName: string;
  lastName: string;
  personalCode?: string;
  role: InjuredRole | null;
};

export enum AccidentType {
  COLLISION_BETWEEN_VEHICLES = "COLLISION_BETWEEN_VEHICLES",
  THEFT_FIRE_VANDALISM = "THEFT_FIRE_VANDALISM",
  GLASS = "GLASS",
  NATURAL_EVENTS = "NATURAL_EVENTS",
  KASKO = "KASKO",
  OTHER = "OTHER",
}

export enum VandalismSeverity {
  PARTIAL_THEFT = "PARTIAL_THEFT",
  TOTAL_THEFT = "TOTAL_THEFT",
  PARTIAL_FIRE = "PARTIAL_FIRE",
  TOTAL_FIRE = "TOTAL_FIRE",
  VANDALISM = "VANDALISM",
}

export enum NaturalEventType {
  HAIL = "HAIL",
  NATURAL_EVENT = "NATURAL_EVENT",
}

export enum InsuranceType {
  KASKO = "KASKO",
  COLLISION = "COLLISION",
}

export enum CaiEnum {
  ONE_SIGNED = "ONE_SIGNED",
  BOTH_SIGNED = "BOTH_SIGNED",
  NOT_AVAILABLE = "NOT_AVAILABLE",
}

export enum Circumstance {
  FAILURE_TO_OBSERVE_SIGNAL = "FAILURE_TO_OBSERVE_SIGNAL",
  COMING_FROM_RIGHT = "COMING_FROM_RIGHT",
  WRONG_WAY_ON_ROAD = "WRONG_WAY_ON_ROAD",
  REVERSING = "REVERSING",
  TURNING_LEFT = "TURNING_LEFT",
  TURNING_RIGHT = "TURNING_RIGHT",
  OVERTAKING = "OVERTAKING",
  CHANGING_LANE = "CHANGING_LANE",
  MOVING_IN_SAME_DIRECTION_DIFFERENT_LANE = "MOVING_IN_SAME_DIRECTION_DIFFERENT_LANE",
  REAR_END_COLLISION = "REAR_END_COLLISION",
  CIRCULATING_IN_ROUNDABOUT = "CIRCULATING_IN_ROUNDABOUT",
  ENTERING_ROUNDABOUT = "ENTERING_ROUNDABOUT",
  ENTERING_PRIVATE_AREA = "ENTERING_PRIVATE_AREA",
  EXITING_PRIVATE_AREA = "EXITING_PRIVATE_AREA",
  PARKING = "PARKING",
  STARTING_AFTER_STOP = "STARTING_AFTER_STOP",
  STATIONARY_VEHICLE = "STATIONARY_VEHICLE",
  OTHER = "OTHER",
}

export enum VehicleType {
  CAR = "CAR",
  MOTORCYCLE = "MOTORCYCLE",
}

export enum ClaimHandlingDecision {
  BODY_SHOP = "BODY_SHOP",
  EXPERT = "EXPERT",
  PHOTO_CAPTURE = "PHOTO_CAPTURE",
}

export enum AuthoritiesIntervention {
  POLICE = "POLICE",
  LOCAL_POLICE = "LOCAL_POLICE",
  CITY_POLICE = "CITY_POLICE",
  OTHER_POLICE = "OTHER_POLICE",
  CARABINIERI = "CARABINIERI",
  FIREFIGHTERS = "FIREFIGHTERS",
  NO = "NO",
}

export enum InjuredRole {
  DRIVER = "DRIVER",
  PASSENGER = "PASSENGER",
}

export enum PointOfImpactGlass {
  FRONT_WINDOW = "FRONT_WINDOW",
  REAR_WINDOW = "REAR_WINDOW",
  LEFT_FRONT_DEFLECTOR = "LEFT_FRONT_DEFLECTOR",
  RIGHT_FRONT_DEFLECTOR = "RIGHT_FRONT_DEFLECTOR",
  LEFT_REAR_DEFLECTOR = "LEFT_REAR_DEFLECTOR",
  RIGHT_REAR_DEFLECTOR = "RIGHT_REAR_DEFLECTOR",
  RIGHT_FRONT_WINDOW = "RIGHT_FRONT_WINDOW",
  LEFT_FRONT_WINDOW = "LEFT_FRONT_WINDOW",
  RIGHT_REAR_WINDOW = "RIGHT_REAR_WINDOW",
  LEFT_REAR_WINDOW = "LEFT_REAR_WINDOW",
}

export enum AdditionalPhotoType {
  CAI = "CAI",
  AUTHORITIES = "AUTHORITIES",
  INJURED = "INJURED",
  VEHICLE_DAMAGE = "VEHICLE_DAMAGE",
  VEHICLE_CORNERS = "VEHICLE_CORNERS",
  OTHER = "OTHER",
}

export enum AccidentResponsibility {
  T = "T", // Total responsibility on PH's vehicle
  R = "R", // Total responsibility on the other vehicle
  C = "C", // Shared responsibility for Vehicle A and Vehicle B
  NC = "NC", // Impossible
}
